import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import {
  Badge,
  Button,
  PageContainer,
  PageHeader,
  Section,
  Skeleton,
} from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import type { AlertItem, AlertSeverity } from '../components/monitoring/AlertFeed';
import type { IrPlaybook } from '../api/types';
import { useRangeAlerts } from '../hooks/useAlerts';
import { useRange } from '../hooks/useRanges';
import { useScenario } from '../hooks/useScenarios';
import {
  useClassifyAlert,
  useSocScore,
  type AlertClassification,
  type TriageAction,
} from '../hooks/useSocTriage';
import { colors } from '../theme/tokens';

export const Route = createFileRoute('/ranges/$id/soc')({
  component: SocTriage,
});

const SEVERITY_VARIANT: Record<AlertSeverity, 'info' | 'warn' | 'danger'> = {
  info: 'info',
  warning: 'warn',
  critical: 'danger',
};

const SEVERITY_PLAYBOOK_COLOR: Record<string, string> = {
  critical: colors.hudRed,
  high: colors.hudAmber,
  medium: colors.hudCyan,
  low: colors.hudCyan,
  informational: colors.hudCyan,
};

/**
 * SOC triage — a clean professional analyst workspace. A PageHeader (breadcrumb
 * back to the range), a calm triage-readout strip, then two labelled Sections:
 * the alert queue (the work surface) and the IR-playbook reference. Trainees
 * classify each detection as a true / false positive. All hooks
 * (useRangeAlerts / useSocScore / useScenario / useClassifyAlert) and the
 * classify mutation + triage actions are unchanged — only the layout + visual
 * layer is new.
 */
function SocTriage() {
  const { id } = Route.useParams();
  const rangeQ = useRange(id);
  const alertsQ = useRangeAlerts(id);
  const scoreQ = useSocScore(id);
  const scenarioSlug = rangeQ.data?.scenarioSlug;
  const scenarioQ = useScenario(scenarioSlug);
  const classify = useClassifyAlert();

  const playbooks: ReadonlyArray<IrPlaybook> = scenarioQ.data?.playbooks ?? [];

  // Which alerts the trainee has already triaged this session (keep them in the
  // queue but mark them resolved so the work-queue feel is preserved).
  const handled = useMemo(() => {
    const m = new Map<string, { classification: AlertClassification; correct: boolean; delta: number }>();
    for (const c of scoreQ.data?.classifications ?? []) {
      m.set(c.alert_id, { classification: c.classification, correct: c.correct, delta: c.delta });
    }
    return m;
  }, [scoreQ.data?.classifications]);

  const alerts = alertsQ.data ?? [];
  const openCount = alerts.filter((a) => !handled.has(a.id)).length;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={ShieldAlert}
        title="SOC triage"
        description="Work the alert queue — classify each detection as a true or false positive. Decoys are benign; only the real kill chain is the attack."
        breadcrumbs={[
          { label: 'Ranges', to: '/ranges' },
          { label: id, to: '/ranges/$id' },
          { label: 'SOC triage' },
        ]}
      />

      {/* ── Triage readout strip ── */}
      <Section title="Your triage score" description="Accuracy and detection coverage for this session.">
        <ScorePanel
          loading={scoreQ.isPending}
          score={scoreQ.data?.soc_score ?? 0}
          accuracy={scoreQ.data?.accuracy ?? 0}
          classified={scoreQ.data?.classified ?? 0}
          tpCaught={scoreQ.data?.tp_caught ?? 0}
          fpDismissed={scoreQ.data?.fp_dismissed ?? 0}
          misses={scoreQ.data?.misses ?? 0}
          open={openCount}
        />
      </Section>

      <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-12">
        {/* Alert queue */}
        <div className="lg:col-span-7">
          <Section
            title="Alert queue"
            description="Classify each detection. Resolved items stay in place with your result."
            action={
              <span className="tabular text-[11px] text-txt-muted">
                {openCount} open · {alerts.length} total
              </span>
            }
          >
            {alertsQ.isPending ? (
              <div className="space-y-2.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={108} radius="lg" />
                ))}
              </div>
            ) : alertsQ.isError ? (
              <div className="hud-panel flex items-start gap-3 border-hud-red/40 p-4">
                <AlertTriangle className="mt-0.5 size-5 shrink-0 text-hud-red" />
                <div>
                  <div className="text-[14px] font-semibold text-txt-bright">Could not load alerts</div>
                  <div className="mt-0.5 font-mono text-[12px] text-txt-2">
                    {(alertsQ.error as Error)?.message ?? 'Unknown error'}
                  </div>
                </div>
              </div>
            ) : alerts.length === 0 ? (
              <div className="hud-panel flex flex-col items-center gap-2.5 px-6 py-12 text-center">
                <span className="flex size-12 items-center justify-center rounded-xl bg-panel text-txt-muted">
                  <ShieldCheck className="size-6" />
                </span>
                <div className="text-[15px] font-semibold text-txt-bright">Queue clear</div>
                <p className="max-w-xs text-[13px] leading-relaxed text-txt-2">
                  No alerts in the queue yet. Start an attack phase to generate detections.
                </p>
              </div>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="show"
                className="space-y-2.5"
              >
                {alerts.map((alert) => (
                  <TriageCard
                    key={alert.id}
                    alert={alert}
                    resolved={handled.get(alert.id)}
                    pending={classify.isPending}
                    onClassify={(classification, action) =>
                      classify.mutate({
                        rangeId: id,
                        alertId: alert.id,
                        classification,
                        action,
                      })
                    }
                  />
                ))}
              </motion.div>
            )}
          </Section>
        </div>

        {/* IR playbooks */}
        <div className="lg:col-span-5">
          <Section
            title="IR playbooks"
            description="Response runbooks defined by this scenario."
            action={
              playbooks.length > 0 ? (
                <Badge variant="info" size="sm">
                  {playbooks.length}
                </Badge>
              ) : null
            }
          >
            {scenarioQ.isPending ? (
              <Skeleton height={220} radius="lg" />
            ) : playbooks.length === 0 ? (
              <div className="hud-panel flex flex-col items-center gap-2 px-6 py-10 text-center">
                <span className="flex size-11 items-center justify-center rounded-xl bg-panel text-txt-muted">
                  <BookOpen className="size-5" />
                </span>
                <p className="text-[13px] text-txt-2">This scenario defines no IR playbooks.</p>
              </div>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="show"
                className="space-y-2.5"
              >
                {playbooks.map((pb) => (
                  <PlaybookCard key={pb.name} playbook={pb} />
                ))}
              </motion.div>
            )}
          </Section>
        </div>
      </div>
    </PageContainer>
  );
}

function ScorePanel({
  loading,
  score,
  accuracy,
  classified,
  tpCaught,
  fpDismissed,
  misses,
  open,
}: {
  loading: boolean;
  score: number;
  accuracy: number;
  classified: number;
  tpCaught: number;
  fpDismissed: number;
  misses: number;
  open: number;
}) {
  if (loading) return <Skeleton height={92} radius="lg" />;
  const accPct = Math.round(accuracy * 100);
  const metrics = [
    { label: 'SOC score', value: String(score), color: score < 0 ? colors.hudRed : colors.neon },
    { label: 'Accuracy', value: `${accPct}%`, color: accPct >= 80 ? colors.neon : colors.hudAmber },
    { label: 'Classified', value: String(classified), color: colors.hudCyan },
    { label: 'TP caught', value: String(tpCaught), color: colors.neon },
    { label: 'FP dismissed', value: String(fpDismissed), color: colors.hudCyan },
    { label: 'Misses', value: String(misses), color: misses ? colors.hudRed : colors.hudCyan },
    { label: 'Open', value: String(open), color: colors.hudAmber },
  ];
  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="show"
      className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7"
    >
      {metrics.map((m) => (
        <motion.div key={m.label} variants={cardItem} className="hud-panel readout p-4">
          <span className="eyebrow">{m.label}</span>
          <span className="readout__value tabular mt-1 text-[24px]" style={{ color: m.color }}>
            {m.value}
          </span>
        </motion.div>
      ))}
    </motion.div>
  );
}

function TriageCard({
  alert,
  resolved,
  pending,
  onClassify,
}: {
  alert: AlertItem;
  resolved: { classification: AlertClassification; correct: boolean; delta: number } | undefined;
  pending: boolean;
  onClassify: (classification: AlertClassification, action: TriageAction) => void;
}) {
  const [action, setAction] = useState<TriageAction>('investigate');
  return (
    <motion.div
      variants={cardItem}
      className={`hud-panel p-4 ${
        resolved ? (resolved.correct ? 'border-neon/30 opacity-80' : 'border-hud-red/30 opacity-80') : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13.5px] font-semibold text-txt-bright">{alert.title}</span>
            <Badge variant={SEVERITY_VARIANT[alert.severity]} size="xs">
              {alert.severity}
            </Badge>
            <Badge variant="neutral" size="xs">
              {alert.source}
            </Badge>
          </div>
          <div className="mt-2 font-mono text-[11.5px] leading-relaxed text-txt-2">{alert.message}</div>
        </div>
        <span className="tabular shrink-0 font-mono text-[11px] text-txt-muted">
          {new Date(alert.ts).toLocaleTimeString()}
        </span>
      </div>

      {resolved ? (
        <div className="mt-3 flex items-center gap-1.5 border-t border-bd pt-3">
          {resolved.correct ? (
            <CheckCircle2 size={15} color={colors.neon} />
          ) : (
            <XCircle size={15} color={colors.hudRed} />
          )}
          <span
            className="text-[12px] font-semibold"
            style={{ color: resolved.correct ? colors.neon : colors.hudRed }}
          >
            {resolved.classification.toUpperCase()} · {resolved.correct ? 'correct' : 'wrong'} ·{' '}
            {resolved.delta >= 0 ? `+${resolved.delta}` : resolved.delta} pts
          </span>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-bd pt-3">
          <div className="flex flex-wrap gap-1">
            {(['investigate', 'escalate', 'isolate', 'dismiss'] as const).map((a) => (
              <Button
                key={a}
                variant={action === a ? 'secondary' : 'ghost'}
                size="xs"
                onClick={() => setAction(a)}
              >
                {a}
              </Button>
            ))}
          </div>
          <div className="flex gap-1.5">
            <Button
              variant="danger"
              size="xs"
              leftIcon={<ShieldAlert size={12} />}
              disabled={pending}
              onClick={() => onClassify('tp', action)}
            >
              True positive
            </Button>
            <Button
              variant="secondary"
              size="xs"
              leftIcon={<ShieldCheck size={12} />}
              disabled={pending}
              onClick={() => onClassify('fp', action === 'investigate' ? 'dismiss' : action)}
            >
              False positive
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function PlaybookCard({ playbook }: { playbook: IrPlaybook }) {
  const accent = SEVERITY_PLAYBOOK_COLOR[playbook.severity ?? 'medium'] ?? colors.hudCyan;
  return (
    <motion.div variants={cardItem} className="hud-panel p-4">
      <div className="flex items-center gap-2">
        <span className="text-[13.5px] font-semibold text-txt-bright">{playbook.name}</span>
        {playbook.severity ? (
          <Badge variant="warn" size="xs">
            {playbook.severity}
          </Badge>
        ) : null}
      </div>
      {playbook.when ? (
        <div className="mt-1.5 text-[12px] leading-relaxed text-txt-2">{playbook.when}</div>
      ) : null}
      <div className="mt-3 space-y-1.5">
        {playbook.steps.map((step, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span
              className="tabular flex size-5 shrink-0 items-center justify-center rounded-md bg-panel font-mono text-[11px] font-semibold"
              style={{ color: accent }}
            >
              {i + 1}
            </span>
            <span className="text-[12.5px] leading-relaxed text-txt-2">{step}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
